import { useState } from "react";
import { Plus } from "lucide-react";
import { useAssetLinks, useCreateAssetLink, useDeleteAssetLink } from "../../../lib/presentationQueries";
import { useFileIndex, useCreateFileRoomLaunch, useRemoveFileLink, useReplaceFile } from "../../../lib/fileQueries";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import FileUploadDialog from "../files/FileUploadDialog";
import FileCard from "../files/FileCard";
import FilePreviewDrawer from "../files/FilePreviewDrawer";
import ReplaceFileInline from "../files/ReplaceFileInline";

/**
 * Master v5 Prompt 11 — real Bunny-backed uploads for a Presentation's
 * Files area (previously a link-only placeholder — Prompt 8's
 * `manual-${Date.now()}` `file_asset_id`). Every upload here is a
 * CONTEXTUAL upload prefilled to this exact presentation — no
 * classification picker, no AudienceGate (both are the General Files
 * page's job). `presentation_asset_links` is still written alongside
 * the real FileAsset so PresentationVersion's own snapshot/compare
 * feature (Prompt 8) keeps working unchanged — `PresentationAssetLink`
 * is a typed view over the canonical FileAssetLink underneath.
 */
export default function FilesArea({ workspaceId, presId, presentationTitle }) {
  const { data: legacyLinks } = useAssetLinks(workspaceId, presId);
  const createAssetLink = useCreateAssetLink(workspaceId, presId);
  const deleteAssetLink = useDeleteAssetLink(workspaceId, presId);
  const { data: fileIndex, refetch } = useFileIndex(workspaceId, { targetType: "presentation", targetId: presId });
  const removeLink = useRemoveFileLink(workspaceId);
  const createRoomLaunch = useCreateFileRoomLaunch(workspaceId);
  const navigate = useNavigate();

  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewFasId, setPreviewFasId] = useState(null);
  const [replacingEntry, setReplacingEntry] = useState(null);

  const items = fileIndex?.items || [];

  async function handleUploaded(result) {
    await createAssetLink.mutateAsync({ file_asset_id: result.asset.fas_id });
    refetch();
  }

  async function handleRemoveLink(fasId, flnId) {
    const legacy = (legacyLinks?.items || []).find((l) => l.file_asset_id === fasId);
    try {
      await removeLink.mutateAsync({ fasId, flnId });
      if (legacy) await deleteAssetLink.mutateAsync(legacy.pal_id);
    } catch {
      toast.error("Couldn't remove that context");
    }
  }

  async function handleOpenRoom(fasId) {
    try {
      const res = await createRoomLaunch.mutateAsync(fasId);
      navigate(`/rehearsal-room/w/${workspaceId}/room/${res.launch_id}`);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't open this file in a Room");
    }
  }

  return (
    <div data-testid="presentation-files-area">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-xl text-ink">Files</h2>
        <button
          onClick={() => setUploadOpen(true)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-terracotta hover:text-terracotta/80"
          data-testid="files-add-link"
        >
          <Plus size={15} /> Upload a file
        </button>
      </div>
      {items.length === 0 && <p className="text-ink-soft text-sm" data-testid="presentation-files-empty">No files uploaded to this presentation yet.</p>}
      <div className="space-y-2" data-testid="presentation-files-list">
        {items.map((entry) => (
          <FileCard
            key={entry.asset.fas_id} entry={entry} workspaceId={workspaceId}
            onOpen={(e) => setPreviewFasId(e.asset.fas_id)} onReplace={setReplacingEntry}
            onRemoveLink={handleRemoveLink} onOpenRoom={handleOpenRoom}
            onRequestFeedback={() => toast.info("Feedback requests are coming soon.")}
          />
        ))}
      </div>

      <FileUploadDialog
        open={uploadOpen} onOpenChange={setUploadOpen} workspaceId={workspaceId}
        context={{ targetType: "presentation", targetId: presId, label: presentationTitle || "this presentation" }}
        onUploaded={handleUploaded}
      />
      <FilePreviewDrawer open={!!previewFasId} onOpenChange={(o) => !o && setPreviewFasId(null)} workspaceId={workspaceId} fasId={previewFasId} />
      {replacingEntry && (
        <ReplaceFileInline workspaceId={workspaceId} entry={replacingEntry} onDone={() => { setReplacingEntry(null); refetch(); }} />
      )}
    </div>
  );
}
